import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getSettings } from '../util/getSettings';
import FilterModal from './filter-modal';
import PriceRange from './filters/price-range';
import SquareFeet from './filters/square-feet';
import HomeTypes from './filters/home-types';
import BedsBaths from './filters/beds-baths';
import ParkingBays from './filters/parking-bays';
import NumberFloors from './filters/number-floors';
import StateFilterDropdown from './filters/state-filter-dropdown';
import MetroFilterDropdown from './filters/metro-filter-dropdown';
import CityFilterDropdown from './filters/city-filter-dropdown';
import MpcFilter from './filters/mpc-filter';
import { getFiltersFromSource } from '../util/filters';
import { QsStateManager } from '../util/urls';
import { startGetCommunities } from '../redux/actions/communities';
import { filterDefaults } from '../util/filters';
import QuickMoveIn from './quick-move-in';
import { resetMobileLocFilters } from '../redux/actions/mobileLocationFilters';
import {
  priceRangeUpdated,
  bedBathsUpdated,
  homeTypesUpdated,
  brandUpdated,
  squareFeetUpdated,
  baysUpdated,
  floorsUpdated,
  mpcOnlyUpdated,
  filtersUpdated,
  resetClicked,
  quickMoveInUpdated
} from '../redux/actions/filters';

const MobileFilterContainer = ({ positionLeft }) => {
  const filterContainerId = `MOBILE_CONTAINER`
  const filters = useSelector((state) => state.filters);
  const communities = useSelector((state) => state.communities);
  const [currentFilters, setCurrentFilters] = useState(getFiltersFromSource(filters));
  const [isOpen, setIsOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [filterChanged, setFilterChanged] = useState(false);
  const labels = getSettings(['century', 'labels'], true);
  const settings = getSettings(['century', 'settings']);
  const dispatch = useDispatch();
  const scrollTop = () => window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
  const mobileLocFilters = useSelector((state) => state.mobileLocFilters);
  const [fetching, setFetching] = useState(false);
  const [fetchingMetros, setFetchingMetros] = useState(false);
  const defaultRegionData = {
    cities: settings.cities, 
    metros: settings.metros, 
    states: settings.searchRegionStates,
    cityName: settings.cityName,
    stateName: settings.stateName,
    metroName: settings.metroName,
  }
  const [regionData, setRegionData] = useState(defaultRegionData)
  
  const {
    minPrice: currentMinPrice,
    maxPrice: currentMaxPrice,
    bedrooms: currentBedrooms,
    bathrooms: currentBathrooms,
    homeTypes: currentHomeTypes,
    brand: currentBrand,
    minSquareFeet: currentMinSquareFeet,
    maxSquareFeet: currentMaxSquareFeet,
    bays: currentBays,
    floors: currentFloors,
    mpcOnly: currentMpcFilter,
    quickMoveIn: currentQmi,
    sortBy: currentSortBy,
    asc: currentAsc
  } = currentFilters;

  const {
    minPrice: defaultMinPrice,
    maxPrice: defaultMaxPrice,
    bedrooms: defaultBedrooms,
    bathrooms: defaultBathrooms,
    homeTypes: defaultHomeTypes,
    brand: defaultBrand,
    minSquareFeet: defaultMinSquareFeet,
    maxSquareFeet: defaultMaxSquareFeet,
    bays: defaultBays,
    floors: defaultFloors,
    mpcOnly: defaultMpcOnly,
    quickMoveIn: defaultQmi,
    sortBy: defaultSortBy,
    asc: defaultAsc
  } = filterDefaults;

  const setLabelsAndCurrentFilters = (
    beds,
    baths,
    minPrice,
    maxPrice,
    homeTypes,
    brand,
    minSqFt,
    maxSqFt,
    bays,
    floors,
    mpc,
    qmi
  ) => {

    setCurrentFilters({
      ...currentFilters,
      minPrice: minPrice,
      maxPrice: maxPrice,
      bedrooms: beds,
      bathrooms: baths,
      homeTypes: homeTypes.toString(),
      brand: brand,
      minSquareFeet: minSqFt,
      maxSquareFeet: maxSqFt,
      bays: bays,
      floors: floors,
      mpcOnly: mpc,
      quickMoveIn: qmi
    });

    setMobileFiltersUiCount({
      currentMinPrice: minPrice,
      currentMaxPrice: maxPrice,
      currentBedrooms: beds,
      currentBathrooms: baths,
      currentBrand: brand,
      currentHomeTypes: homeTypes.toString(),
      currentMinSquareFeet: minSqFt,
      currentMaxSquareFeet: maxSqFt,
      currentBays: bays,
      currentFloors: floors,
      currentMpcFilter: mpc,
      currentQmi: qmi
    });
  };

  useEffect(() => {
    setLabelsAndCurrentFilters(
      currentBedrooms,
      currentBathrooms,
      currentMinPrice,
      currentMaxPrice,
      currentHomeTypes,
      currentBrand,
      currentMinSquareFeet,
      currentMaxSquareFeet,
      currentBays,
      currentFloors,
      currentMpcFilter,
      currentQmi
    );
  }, []);

  useEffect(() => {
    setLabelsAndCurrentFilters(
      filters.bedrooms,
      filters.bathrooms,
      filters.minPrice,
      filters.maxPrice,
      filters.homeTypes,
      filters.brand,
      filters.minSquareFeet,
      filters.maxSquareFeet,
      filters.bays,
      filters.floors,
      filters.mpcOnly,
      filters.quickMoveIn
    );
  }, [filters.timeStamp, regionData.timeStamp]);

  const toggleFilter = () => {
    setIsOpen(!isOpen);
  };

  const closeFilter = () => {
    setIsOpen(false);
    setCurrentFilters(filters);
    dispatch(resetMobileLocFilters())
    setRegionData(defaultRegionData)
  };

  const setFilterStates = (labelValue, filterChangedValue, openValue) => {
    setLabel(labelValue);
    setFilterChanged(filterChangedValue);
    setIsOpen(openValue);
  };

  //QMI
  const onQmiChange = (qmi) => {
    console.log(qmi)
    setCurrentFilters({...currentFilters, quickMoveIn: qmi})
  }

  //PRICE RANGE
  const onPriceRangeChange = (minPrice, maxPrice) => {
    setCurrentFilters((prevState) => ({ ...prevState, minPrice: minPrice, maxPrice: maxPrice }));
  };

  //BEDS BATHS
  const onBedBathChange = (bedrooms, bathrooms) => {
    setCurrentFilters({ ...currentFilters, bedrooms: bedrooms, bathrooms: bathrooms });
  };

  //HOME TYPES
  const onHomeTypesChange = (homeTypes) => {
    setCurrentFilters({ ...currentFilters, homeTypes: homeTypes.toString() });
  };

  //MORE FILTERS
  const onSquareFeetRangeChange = (minFeet, maxFeet) => {
    setCurrentFilters((prevState) => ({ ...prevState, minSquareFeet: minFeet, maxSquareFeet: maxFeet }));
  };

  const onParkingBaysChange = (bays) => {
    setCurrentFilters({ ...currentFilters, bays: bays });
  };

  const onFloorsChange = (floors) => {
    setCurrentFilters({ ...currentFilters, floors: floors });
  };

  const onMpcFilterChange = (mpcOnly) => {
    setCurrentFilters({ ...currentFilters, mpcOnly: mpcOnly });
  };

  //MOBILE FILTERS
  const setMobileFiltersUiCount = (params) => {
    let filtersChangedCount = 0;
    if (params.currentMinPrice !== defaultMinPrice || params.currentMaxPrice !== defaultMaxPrice) {
      filtersChangedCount++;
    }
    if (params.currentBedrooms !== defaultBedrooms || params.currentBathrooms !== defaultBathrooms) {
      filtersChangedCount++;
    }
    if (params.currentBrand !== defaultBrand) {
      filtersChangedCount++;
    }
    if (params.currentHomeTypes !== defaultHomeTypes) {
      filtersChangedCount++;
    }
    if (params.currentMinSquareFeet !== defaultMinSquareFeet || params.currentMaxSquareFeet !== defaultMaxSquareFeet) {
      filtersChangedCount++;
    }
    if (params.currentBays !== defaultBays) {
      filtersChangedCount++;
    }
    if (params.currentFloors !== defaultFloors) {
      filtersChangedCount++;
    }
    if (params.currentMpcFilter) {
      filtersChangedCount++;
    }
    if (params.currentQmi !== defaultQmi) {
      filtersChangedCount++;
    }

    if (filtersChangedCount > 0) {
      setFilterStates(filtersChangedCount, true, false);
    } else {
      setFilterStates('', false, false);
    }
  };

  const onMobileSave = () => {
    const queryStateManager = new QsStateManager()
        .addParam('np', currentMinPrice)
        .addParam('xp', currentMaxPrice)
        .addParam('br', currentBedrooms)
        .addParam('ba', currentBathrooms)
        .addParam('ht', currentHomeTypes)
        .addParam('nd', currentBrand === undefined ? '' : currentBrand)
        .addParam('nf', currentMinSquareFeet)
        .addParam('xf', currentMaxSquareFeet)
        .addParam('by', currentBays)
        .addParam('fl', currentFloors)
        .addParam('pc', currentMpcFilter)
        .addParam('qm', currentQmi)
        .addFilters(filters);

    scrollTop();
    toggleFilter();

    if (mobileLocFilters.relativeUrl.length) {
      queryStateManager.goToNewPage(mobileLocFilters.relativeUrl);
      dispatch(startGetCommunities());
    } else {
      dispatch(bedBathsUpdated(currentBedrooms, currentBathrooms));
      dispatch(brandUpdated(currentBrand));
      dispatch(homeTypesUpdated(currentHomeTypes));
      dispatch(squareFeetUpdated(currentMinSquareFeet, currentMaxSquareFeet));
      dispatch(baysUpdated(currentBays));
      dispatch(floorsUpdated(currentFloors));
      dispatch(priceRangeUpdated(currentMinPrice, currentMaxPrice));
      dispatch(mpcOnlyUpdated(currentMpcFilter));
      dispatch(quickMoveInUpdated(currentQmi));
      dispatch(filtersUpdated());
      queryStateManager.doUpdate();
    }
  };

  const onMobileClear = () => {
    new QsStateManager()
      .addParam('np', defaultMinPrice)
      .addParam('xp', defaultMaxPrice)
      .addParam('br', defaultBedrooms)
      .addParam('ba', defaultBathrooms)
      .addParam('ht', '')
      .addParam('nd', '')
      .addParam('nf', defaultMinSquareFeet)
      .addParam('xf', defaultMaxSquareFeet)
      .addParam('by', defaultBays)
      .addParam('fl', defaultFloors)
      .addParam('pc', defaultMpcOnly)
      .addParam('qm', defaultQmi)
      .addFilters(filters)
      .doUpdate();
    dispatch(priceRangeUpdated(defaultMinPrice, defaultMaxPrice));
    dispatch(bedBathsUpdated(defaultBedrooms, defaultBathrooms));
    dispatch(brandUpdated(defaultBrand));
    dispatch(homeTypesUpdated(defaultHomeTypes));
    dispatch(squareFeetUpdated(defaultMinSquareFeet, defaultMaxSquareFeet));
    dispatch(baysUpdated(defaultBays));
    dispatch(floorsUpdated(defaultFloors));
    dispatch(mpcOnlyUpdated(defaultMpcOnly));
    dispatch(resetClicked());
    setCurrentFilters(filterDefaults);
    dispatch(resetMobileLocFilters())
    setRegionData(defaultRegionData)
    dispatch(quickMoveInUpdated(defaultQmi));
    scrollTop();
    toggleFilter();
  };

  return (
    <div className="filter-container">
      <button
        className={'filter-button mobile'}
        onClick={toggleFilter}
        aria-expanded={isOpen}
        aria-controls={filterContainerId}
      >
        <span className='fa-solid fa-sliders' aria-hidden="true"></span>
        <span className='sr-only'>{labels.openMobileFilters.length ? labels.openMobileFilters : 'Open'}</span>
      </button>
      <div className="total-results" aria-live="assertive">
        {communities.isFetching && (
          <div>
            <span className="spinner-border resultsContainer__spinner"></span>
            {labels.loadingMessageLabel}
          </div>
        )}
        {!communities.isFetching && communities.timeStamp !== null && (
          <div>
            <span>{communities.response.data.total}</span> Results
          </div>
        )}
      </div>
      <FilterModal
        open={isOpen}
        closeModal={closeFilter}
        onSave={onMobileSave}
        onClear={onMobileClear}
        positionLeft={positionLeft}
        hideFooter={false}
        mobile={true}
        displayCloseBtn={true}
        containerId={filterContainerId}
      >
        <div className="mobile-filters-container">
            <div className="mobile-filters-title">
              Filter Your Search
              <div className="mobile-toggle-container">
                <QuickMoveIn onChange={onQmiChange} mobile />
              </div>
            </div>
            <StateFilterDropdown setFetching={setFetching} />
            {fetching ? (
              <div className='dropdown'>
                <span className="spinner-border"></span><span>Loading...</span>
              </div>
            ) : (
              <MetroFilterDropdown setFetchingMetros={setFetchingMetros} />
            )}
            
            {fetching || fetchingMetros ? (
              <div className='dropdown'>
                <span className="spinner-border"></span><span>Loading...</span>
              </div>
            ) : (
              <CityFilterDropdown />
            )}
            
            <PriceRange minPrice={currentMinPrice} maxPrice={currentMaxPrice} onChange={onPriceRangeChange} />
            <BedsBaths bedCount={currentBedrooms} bathCount={currentBathrooms} onChange={onBedBathChange} />
            <HomeTypes onChange={onHomeTypesChange} selectedHomeTypes={currentHomeTypes} />
            <div>
              <SquareFeet
                minFeet={currentMinSquareFeet}
                maxFeet={currentMaxSquareFeet}
                onChange={onSquareFeetRangeChange}
              />
              <div className="filter-row align-items-stretch">
                <ParkingBays currentBays={currentBays} onChange={onParkingBaysChange} />
                <NumberFloors currentFloors={currentFloors} onChange={onFloorsChange} />
              </div>
            </div>
            <div>
              <MpcFilter currentMpcFilter={currentMpcFilter} onChange={onMpcFilterChange} />
            </div>
          </div>
      </FilterModal>
    </div>
  );
};

export default MobileFilterContainer;
